import { type DatePickerFormat } from '@saviynt/design-system';
import {
  addYears,
  format as dateFnsFormat,
  isValid as dateFnsIsValid,
  parse as dateFnsParse,
  subYears,
} from 'date-fns';

import { DATE_FORMATS, DATE_RANGE_LIMITS } from '../constants';

/**
 * Converts a field dateFormat (display format) to the corresponding picker format
 * @param fieldDateFormat - The date format from the field configuration (e.g., 'MM-DD-YYYY')
 * @returns The corresponding picker format (e.g., 'MM-dd-yyyy')
 *
 * Updated: The DatePicker component now supports all three date formats.
 * Each display format correctly maps to its corresponding picker format.
 */
export const convertToPickerDateFormat = (
  fieldDateFormat: string
): DatePickerFormat => {
  // Find the format object that matches either display or picker format
  const formatEntry = Object.values(DATE_FORMATS).find(
    (fmt) => fmt.display === fieldDateFormat || fmt.picker === fieldDateFormat
  );

  // Return the picker format, or fallback to MM-dd-yyyy
  return (formatEntry?.picker ?? 'MM-dd-yyyy') as DatePickerFormat;
};

// Converts picker format to display format for placeholder
export const convertToDisplayFormat = (pickerFormat: string): string => {
  const formatEntry = Object.values(DATE_FORMATS).find(
    (fmt) => fmt.picker === pickerFormat
  );

  return formatEntry?.display ?? pickerFormat;
};

/**
 * Gets the default minimum date (today - specified years)
 * This ensures the date doesn't get deprecated over time
 * @param yearsBack - Number of years to subtract from today (default: 100)
 * @returns Date object representing the minimum date
 */
export const getDefaultMinDate = (
  yearsBack: number = DATE_RANGE_LIMITS.DEFAULT_MIN_YEARS_BACK
): Date => subYears(new Date(), yearsBack);

/**
 * Gets the default maximum date (today + specified years)
 * This ensures the date doesn't get deprecated over time
 * @param yearsForward - Number of years to add to today (default: 100)
 * @returns Date object representing the maximum date
 */
export const getDefaultMaxDate = (
  yearsForward: number = DATE_RANGE_LIMITS.DEFAULT_MAX_YEARS_FORWARD
): Date => addYears(new Date(), yearsForward);

/**
 * Gets the picker minimum date for properties panel (today - specified years)
 * @param yearsBack - Number of years to subtract from today (default: 00)
 * @returns Date object representing the picker minimum date
 */
export const getPickerMinDate = (
  yearsBack: number = DATE_RANGE_LIMITS.PICKER_MIN_YEARS_BACK
): Date => subYears(new Date(), yearsBack);

/**
 * Gets the picker maximum date for properties panel (today + specified years)
 * @param yearsForward - Number of years to add to today (default: 100)
 * @returns Date object representing the picker maximum date
 */
export const getPickerMaxDate = (
  yearsForward: number = DATE_RANGE_LIMITS.PICKER_MAX_YEARS_FORWARD
): Date => addYears(new Date(), yearsForward);

/**
 * Converts string dates to Date objects
 * @param dateValue - Date value that can be string, Date, null, or undefined
 * @returns Date object or null
 */
export const convertToDate = (
  dateValue: Date | string | null | undefined
): Date | null => {
  if (!dateValue) {
    return null;
  }

  const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;

  // Check if date is valid
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date;
  }

  return null; // Return null for invalid dates instead of Invalid Date object
};

/**
 * Strictly validates a manually entered date string against a picker format.
 * Returns true only if the text matches the format exactly and represents a real date.
 *
 * Examples (format 'dd-MM-yyyy'):
 *   '01-01-2026' → true
 *   '29-02-2026' → false (not a leap year)
 *   '01012026'   → false (missing separators)
 *   '2026-01-01' → false (wrong format)
 */
export const validateManualDateInput = (
  text: string,
  pickerFormat: string
): boolean => {
  if (!text.trim()) {
    return true; // empty is handled by required validation
  }

  const parsed = dateFnsParse(text.trim(), pickerFormat, new Date());

  if (!dateFnsIsValid(parsed)) {
    return false;
  }

  // Round-trip check: format the parsed date back and compare to input
  // This catches wrong separators, wrong order, and out-of-range values
  return dateFnsFormat(parsed, pickerFormat) === text.trim();
};
